import { Box, Card, Skeleton, Typography } from '@mui/material';
import {
  CalendarMonthOutlined,
  CheckCircleOutlined,
  PercentOutlined,
  SchoolOutlined,
  TimerOutlined,
} from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/system';
import { colors } from 'Constants/Colors';
import useTranslation from 'Hooks/useTranslation';
import type { SessionsReportKpisI } from 'Api/axios/sessions-report-api';

interface Props {
  kpis: SessionsReportKpisI | null;
  loading: boolean;
}

const styles: Record<string, SxProps<Theme>> = {
  grid: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
    gap: 2,
    mb: 2.5,
  },
  card: {
    p: 2.25,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    borderRadius: 2,
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    border: `1px solid ${colors.GRAY_HR}`,
  },
  value: { fontSize: 22, fontWeight: 700, color: colors.BLACK, lineHeight: 1.1 },
  label: { fontSize: 12.5, color: colors.GRAY_DARK, mt: 0.5 },
  sublabel: { fontSize: 11, color: colors.GRAY_DARK, mt: 0.25 },
};

const iconWrapStyle = (tint: string): SxProps<Theme> => ({
  width: 42,
  height: 42,
  borderRadius: 1.25,
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0,
  background: `${tint}1A`,
  color: tint,
});

/**
 * Pure presentational: the KPIs come precomputed from the backend
 * (`kpis` field of the report response). Frontend renders, never aggregates.
 */
export default function SessionsReportKpis({ kpis, loading }: Props) {
  const { t2 } = useTranslation('SessionsReport');

  const items = [
    {
      key: 'total',
      tint: colors.GREEN,
      icon: <CalendarMonthOutlined />,
      value: kpis?.totalSessions ?? 0,
      label: t2('kpis.totalSessions'),
    },
    {
      key: 'completed',
      tint: colors.GREEN_LEAF,
      icon: <CheckCircleOutlined />,
      value: kpis?.completedSessions ?? 0,
      label: t2('kpis.completedSessions'),
      sublabel: t2('kpis.completedPercentage', [String(kpis?.completedPercentage ?? 0)]),
    },
    {
      key: 'attendance',
      tint: colors.BROWN,
      icon: <PercentOutlined />,
      value: `${kpis?.averageAttendancePercentage ?? 0}%`,
      label: t2('kpis.averageAttendance'),
      sublabel: t2('kpis.attendanceRatio', [String(kpis?.presentAttendees ?? 0), String(kpis?.totalAttendees ?? 0)]),
    },
    {
      key: 'hours',
      tint: colors.BLUE,
      icon: <TimerOutlined />,
      value: `${kpis?.totalHours ?? 0}h`,
      label: t2('kpis.totalHours'),
    },
    {
      key: 'courses',
      tint: colors.PURPLE,
      icon: <SchoolOutlined />,
      value: kpis?.involvedCourses ?? 0,
      label: t2('kpis.involvedCourses'),
    },
  ];

  return (
    <Box sx={styles.grid}>
      {items.map(item => (
        <Card key={item.key} sx={styles.card} elevation={0}>
          <Box sx={iconWrapStyle(item.tint)}>{item.icon}</Box>
          <Box>
            {loading && !kpis ? (
              <Skeleton variant="text" width={60} height={28} />
            ) : (
              <Typography sx={styles.value}>{item.value}</Typography>
            )}
            <Typography sx={styles.label}>{item.label}</Typography>
            {item.sublabel && <Typography sx={styles.sublabel}>{item.sublabel}</Typography>}
          </Box>
        </Card>
      ))}
    </Box>
  );
}
